import { keepTogether, MISSING_NUMBER, numberLocale } from './locale';

export interface MoneyFormatOptions {
  /** ISO 4217 code; empty → EUR. */
  currency?: string | null;
  /** Show the sign for positive amounts too, e.g. price changes "+12,50 €". */
  signed?: boolean;
  /** Without cents, e.g. plan prices "49 €". */
  wholeUnits?: boolean;
  /** UI language if the caller carries it explicitly; otherwise the current i18n language. */
  lang?: string;
}

const formatters = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, currency: string, signed: boolean, wholeUnits: boolean): Intl.NumberFormat {
  const key = `${locale}|${currency}|${signed}|${wholeUnits}`;
  let cached = formatters.get(key);
  if (!cached) {
    const digits = wholeUnits ? 0 : 2;
    cached = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      signDisplay: signed ? 'exceptZero' : 'auto',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    formatters.set(key, cached);
  }
  return cached;
}

/**
 * The single formatter for money amounts: "3.060,00 €" (de), "€3,060.00" (en), "3060,00 €" (sl).
 * Follows the UI language; amount and currency never wrap apart. A missing amount renders as
 * "—", not "0,00 €", so an unpriced position does not look free of charge.
 */
export function formatMoney(amount?: number | null, options: MoneyFormatOptions = {}): string {
  if (amount == null) return MISSING_NUMBER;
  const { currency, signed = false, wholeUnits = false, lang } = options;
  return keepTogether(formatter(numberLocale(lang), currency || 'EUR', signed, wholeUnits).format(amount));
}
